import type { ManagementAction } from "../types/tradeManagementReport";
import { exitPlacementSliceDisplayLabel } from "./exitPlacementDisplay";
import { humanizeManagementActions } from "./managementActionLabels";
import { normalizeJsonPointer } from "./reportEvidencePointer";
import { parseTesFocusEvidencePointer } from "./tesEvidencePointers";

function titleCase(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function leafSegment(pointer: string): string {
  const parts = pointer.split("/").filter(Boolean);
  const last = parts[parts.length - 1] ?? "";
  if (/^\d+$/.test(last) && parts.length >= 2) {
    return parts[parts.length - 2] ?? last;
  }
  return last;
}

const TES_FOCUS_SUBPATH_LABELS: Record<string, string> = {
  "excursion_card/headline": "Headline",
  "excursion_card/pct_losers_strong_favorable_run_atr": "% losers strong favorable run",
  "excursion_card/subheadline": "Subheadline",
  segment_label: "Segment label",
  display_label: "Display label",
  sample_size: "Sample size",
  "exit_placement/display/plain_summary": "Exit placement summary",
  "exit_placement/no_action_reason": "Why no SL/TP",
  "exit_placement/rationale": "Exit placement rationale",
  "exit_placement/display/sl_distance_price": "Stop distance",
  "exit_placement/display/tp_distance_price": "Target distance",
  "exit_placement/display/tp_d": "Target distance",
  "exit_placement/display/sl_d": "Stop distance",
  "trade_management_summary/loser_excursion_profile/median_loser_favorable_move_atr":
    "Median loser favorable move (ATR)",
  "trade_management_summary/trailing_safety/recommended_activation_mfe_atr_floor":
    "Recommended activation MFE ATR floor (legacy)",
  "trade_management_summary/distributions_atr/winners/mae_atr/median": "Winner MAE median (ATR)",
  "trade_management_summary/price_movement_profile/suggested_stop_atr_winners":
    "Suggested stop (winners, ATR)",
  "trade_management_summary/price_movement_profile/suggested_take_profit_atr":
    "Suggested take-profit (ATR)",
  simulation_rule: "Simulation rule",
};

function tesFocusSubpathTitle(pointer: string): string | null {
  const parsed = parseTesFocusEvidencePointer(normalizeJsonPointer(pointer));
  if (!parsed) return null;
  const sub = parsed.subPath.replace(/^\//, "");
  if (TES_FOCUS_SUBPATH_LABELS[sub]) return TES_FOCUS_SUBPATH_LABELS[sub];
  if (sub.startsWith("management_actions/")) return "Suggested management actions";
  return null;
}

/** Human label for a JSON pointer into the report snapshot (not the parent collection name). */
export function evidencePointerTitle(pointer: string): string {
  const cleaned = (pointer || "").trim();
  if (!cleaned) return "Evidence";

  const tesTitle = tesFocusSubpathTitle(cleaned);
  if (tesTitle) return tesTitle;

  const parts = cleaned.split("/").filter(Boolean);
  if (parts[0] === "tes_focus_slices") {
    const leaf = leafSegment(cleaned).replace(/[_-]+/g, " ");
    return titleCase(leaf || "Slice evidence");
  }
  if (parts[0] === "management_candidates") {
    const leaf = leafSegment(cleaned);
    const labels: Record<string, string> = {
      action_rationale: "Action rationale",
      management_actions: "Suggested management actions",
      price_movement_profile: "Price movement profile",
      sample_size: "Sample size",
      expectancy: "Expectancy",
      win_rate: "Win rate",
      total_pnl: "Total PnL",
      losers: "Swing profile (losers)",
      winners: "Swing profile (winners)",
    };
    if (labels[leaf]) return labels[leaf];
    if (parts.includes("swing_management_profile")) {
      const side = parts[parts.length - 1];
      if (side === "losers" || side === "winners") {
        return labels[side] ?? "Swing profile";
      }
      return "Swing profile";
    }
  }

  if (parts[0] === "stats" && parts[1] === "regime_actionable_filters") {
    const list = parts[2]?.replace(/_/g, " ") ?? "filter";
    return `Actionable filters · ${list}`;
  }
  if (parts[0] === "stats" && parts[1] === "exit_placement_priority_leaks") {
    if (parts[2] === "leaks" && parts[3] && /^\d+$/.test(parts[3])) {
      return `Top leak context #${Number(parts[3]) + 1}`;
    }
    return "Exit placement priority leaks";
  }
  if (parts.includes("exit_placement") && parts.includes("plain_summary")) {
    return "Exit placement summary";
  }
  if (parts.includes("exit_placement") && parts.includes("no_action_reason")) {
    return "Why no SL/TP";
  }
  if (parts.includes("best_situations")) return "Market condition: best situation";
  if (parts.includes("worst_situations")) return "Market condition: worst situation";

  const leaf = leafSegment(cleaned).replace(/[_-]+/g, " ");
  return titleCase(leaf || cleaned);
}

function formatStringArray(items: unknown[]): string {
  const lines = items
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
  return lines.join("\n\n");
}

function formatPriceMovementProfile(obj: Record<string, unknown>): string {
  const lines: string[] = [];
  const pctStrong = obj.pct_losers_strong_favorable_run_atr;
  const pctPullback = obj.pct_losers_favorable_first_then_deep_pullback;
  const tpAtr = obj.suggested_take_profit_atr ?? obj.implied_reward_atr_losers_p75;
  const slAtr = obj.suggested_stop_atr_winners ?? obj.implied_risk_atr_median;
  const rr = obj.min_reward_risk_ratio;

  if (typeof pctStrong === "number" && Number.isFinite(pctStrong)) {
    lines.push(`${pctStrong.toFixed(0)}% of losers had a strong favorable run (≥0.8× ATR MFE).`);
  }
  if (typeof pctPullback === "number" && Number.isFinite(pctPullback)) {
    lines.push(
      `${pctPullback.toFixed(0)}% of losers ran favorably first, then had a deep pullback after the peak.`
    );
  }
  if (typeof tpAtr === "number" && Number.isFinite(tpAtr)) {
    lines.push(`Suggested take-profit distance: ~${tpAtr.toFixed(2)}× ATR.`);
  }
  if (typeof slAtr === "number" && Number.isFinite(slAtr)) {
    lines.push(`Suggested stop distance (from price movement): ~${slAtr.toFixed(2)}× ATR.`);
  }
  if (typeof rr === "number" && Number.isFinite(rr)) {
    lines.push(`Minimum reward:risk target: ${rr.toFixed(1)}:1.`);
  }

  if (lines.length) return lines.join("\n\n");
  return formatObjectFallback(obj);
}

function formatExitPlacementPriorityLeak(obj: Record<string, unknown>): string {
  const lines: string[] = [];
  const label = exitPlacementSliceDisplayLabel(obj as Parameters<typeof exitPlacementSliceDisplayLabel>[0]);
  if (label) lines.push(label);

  const stats: string[] = [];
  if (typeof obj.sample_size === "number" && Number.isFinite(obj.sample_size)) {
    stats.push(`${Math.round(obj.sample_size)} trades`);
  }
  if (typeof obj.expectancy === "number" && Number.isFinite(obj.expectancy)) {
    stats.push(`expectancy ${obj.expectancy.toFixed(2)}`);
  }
  if (typeof obj.total_pnl === "number" && Number.isFinite(obj.total_pnl)) {
    const sign = obj.total_pnl >= 0 ? "+" : "";
    stats.push(`PnL ${sign}${obj.total_pnl.toFixed(2)}`);
  }
  if (stats.length) lines.push(stats.join(" · "));

  const summary = String(obj.plain_summary ?? obj.no_action_reason ?? "").trim();
  if (summary) lines.push(summary);

  if (lines.length) return lines.join("\n\n");
  return formatObjectFallback(obj);
}

export type EvidenceTableRow = {
  label: string;
  value: string;
};

function objectFallbackRows(obj: Record<string, unknown>): EvidenceTableRow[] {
  const entries = Object.entries(obj).filter(([, v]) => v != null && v !== "");
  if (!entries.length) return [{ label: "Status", value: "No data." }];
  return entries.map(([k, v]) => ({
    label: titleCase(k.replace(/_/g, " ")),
    value: formatScalarCellValue(k, v),
  }));
}

function formatScalarCellValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number" && Number.isFinite(value)) {
    const normalizedKey = key.replace(/_/g, " ");
    if (key === "win_rate" && value >= 0 && value <= 1) {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (key === "sample_size") return `${Math.round(value)} trades`;
    if (key === "expectancy") return value.toFixed(2);
    if (key === "total_pnl") {
      const sign = value >= 0 ? "+" : "";
      return `${sign}${value.toFixed(2)}`;
    }
    if (normalizedKey.includes("pct losers strong favorable run atr")) {
      return `${value.toFixed(0)}% of losers had a strong favorable run (≥0.8× ATR MFE).`;
    }
    return String(value);
  }
  if (typeof value === "string") return value.trim() || "—";
  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === "string")) {
      return formatStringArray(value);
    }
    return value.map((item) => String(item ?? "")).join("\n");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function priceMovementProfileRows(obj: Record<string, unknown>): EvidenceTableRow[] {
  const rows: EvidenceTableRow[] = [];
  const pctStrong = obj.pct_losers_strong_favorable_run_atr;
  const pctPullback = obj.pct_losers_favorable_first_then_deep_pullback;
  const tpAtr = obj.suggested_take_profit_atr ?? obj.implied_reward_atr_losers_p75;
  const slAtr = obj.suggested_stop_atr_winners ?? obj.implied_risk_atr_median;
  const rr = obj.min_reward_risk_ratio;

  if (typeof pctStrong === "number" && Number.isFinite(pctStrong)) {
    rows.push({
      label: "Strong favorable run",
      value: `${pctStrong.toFixed(0)}% of losers (≥0.8× ATR MFE)`,
    });
  }
  if (typeof pctPullback === "number" && Number.isFinite(pctPullback)) {
    rows.push({
      label: "Favorable then pullback",
      value: `${pctPullback.toFixed(0)}% of losers ran favorably first, then had a deep pullback after the peak`,
    });
  }
  if (typeof tpAtr === "number" && Number.isFinite(tpAtr)) {
    rows.push({
      label: "Suggested take-profit",
      value: `~${tpAtr.toFixed(2)}× ATR`,
    });
  }
  if (typeof slAtr === "number" && Number.isFinite(slAtr)) {
    rows.push({
      label: "Suggested stop",
      value: `~${slAtr.toFixed(2)}× ATR`,
    });
  }
  if (typeof rr === "number" && Number.isFinite(rr)) {
    rows.push({
      label: "Minimum reward:risk",
      value: `${rr.toFixed(1)}:1`,
    });
  }

  return rows.length ? rows : objectFallbackRows(obj);
}

function exitPlacementPriorityLeakRows(obj: Record<string, unknown>): EvidenceTableRow[] {
  const rows: EvidenceTableRow[] = [];
  const label = exitPlacementSliceDisplayLabel(
    obj as Parameters<typeof exitPlacementSliceDisplayLabel>[0]
  );
  if (label) rows.push({ label: "Segment", value: label });

  if (typeof obj.sample_size === "number" && Number.isFinite(obj.sample_size)) {
    rows.push({ label: "Sample size", value: `${Math.round(obj.sample_size)} trades` });
  }
  if (typeof obj.expectancy === "number" && Number.isFinite(obj.expectancy)) {
    rows.push({ label: "Expectancy", value: obj.expectancy.toFixed(2) });
  }
  if (typeof obj.total_pnl === "number" && Number.isFinite(obj.total_pnl)) {
    const sign = obj.total_pnl >= 0 ? "+" : "";
    rows.push({ label: "Total PnL", value: `${sign}${obj.total_pnl.toFixed(2)}` });
  }

  const summary = String(obj.plain_summary ?? obj.no_action_reason ?? "").trim();
  if (summary) rows.push({ label: "Summary", value: summary });

  return rows.length ? rows : objectFallbackRows(obj);
}

function formatEvidenceNumberRow(pointer: string, value: number): EvidenceTableRow {
  const leaf = leafSegment(pointer);
  const normalized = normalizeJsonPointer(pointer);
  if (leaf === "win_rate" && value >= 0 && value <= 1) {
    return { label: "Win rate", value: `${(value * 100).toFixed(1)}%` };
  }
  if (leaf === "sample_size") return { label: "Sample size", value: `${Math.round(value)} trades` };
  if (leaf === "expectancy") return { label: "Expectancy", value: value.toFixed(2) };
  if (leaf === "total_pnl") {
    const sign = value >= 0 ? "+" : "";
    return { label: "Total PnL", value: `${sign}${value.toFixed(2)}` };
  }
  if (normalized.includes("pct_losers_strong_favorable_run_atr")) {
    return {
      label: "Strong favorable run",
      value: `${value.toFixed(0)}% of losers (≥0.8× ATR MFE)`,
    };
  }
  if (normalized.includes("median_loser_favorable_move_atr")) {
    return { label: "Median loser favorable move", value: `${value.toFixed(2)}× ATR` };
  }
  if (normalized.includes("recommended_activation_mfe_atr_floor")) {
    return {
      label: "Trail activation floor",
      value: `${value.toFixed(2)}× ATR MFE`,
    };
  }
  return { label: evidencePointerTitle(pointer), value: String(value) };
}

/** Key/value rows for evidence pointer values (paired with report evidence tables). */
export function buildEvidenceTableRows(pointer: string, value: unknown): EvidenceTableRow[] {
  if (value === undefined) {
    return [
      {
        label: "Status",
        value: "No matching evidence was found in the snapshot for this pointer.",
      },
    ];
  }
  if (value === null) return [{ label: "Status", value: "The snapshot value is empty." }];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [{ label: "Status", value: "The snapshot value is empty." }];
    if (trimmed.includes("\n")) {
      return trimmed
        .split(/\n\n+/)
        .map((para, index) => ({
          label: index === 0 ? "Detail" : `Detail ${index + 1}`,
          value: para.trim(),
        }))
        .filter((row) => row.value);
    }
    return [{ label: "Detail", value: trimmed }];
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return [formatEvidenceNumberRow(pointer, value)];
  }

  if (typeof value === "boolean") {
    return [{ label: evidencePointerTitle(pointer), value: value ? "Yes" : "No" }];
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return [{ label: "Status", value: "The list is empty." }];
    if (value.every((item) => typeof item === "string")) {
      return value
        .map((item, index) => ({
          label: `Item ${index + 1}`,
          value: String(item).trim(),
        }))
        .filter((row) => row.value);
    }
    return value.map((item, index) => ({
      label: `Item ${index + 1}`,
      value:
        typeof item === "object" && item !== null
          ? JSON.stringify(item)
          : String(item ?? ""),
    }));
  }

  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const parts = pointer.split("/").filter(Boolean);
    const leaf = parts[parts.length - 1] ?? "";

    if (parts.includes("management_actions") || leaf === "management_actions") {
      const actions = humanizeManagementActions(
        Array.isArray(value) ? (value as ManagementAction[]) : undefined
      );
      if (!actions.length) return [{ label: "Actions", value: "No management actions." }];
      return actions.map((action, index) => ({
        label: `Action ${index + 1}`,
        value: action,
      }));
    }

    if (parts.includes("price_movement_profile")) {
      return priceMovementProfileRows(obj);
    }

    if (parts.includes("simulation_rule") && obj && typeof obj === "object") {
      const rows: EvidenceTableRow[] = [];
      const name = typeof obj.name === "string" ? obj.name.trim() : "";
      const kind = typeof obj.kind === "string" ? obj.kind.trim() : "";
      if (name) rows.push({ label: "Name", value: name });
      if (kind) rows.push({ label: "Kind", value: kind });
      return rows.length ? rows : objectFallbackRows(obj);
    }

    if (parts.includes("excursion_card") && typeof obj.headline === "string" && obj.headline.trim()) {
      const rows: EvidenceTableRow[] = [{ label: "Headline", value: obj.headline.trim() }];
      if (typeof obj.subheadline === "string" && obj.subheadline.trim()) {
        rows.push({ label: "Subheadline", value: obj.subheadline.trim() });
      }
      return rows;
    }

    if (parts.includes("exit_placement_priority_leaks") && parts.includes("leaks")) {
      return exitPlacementPriorityLeakRows(obj);
    }

    if (parts.includes("swing_management_profile") || parts.includes("swing_behavior")) {
      return objectFallbackRows(obj);
    }

    return objectFallbackRows(obj);
  }

  return [{ label: "Value", value: String(value) }];
}

function formatObjectFallback(obj: Record<string, unknown>): string {
  const entries = Object.entries(obj).filter(([, v]) => v != null && v !== "");
  if (!entries.length) return "No data.";
  return entries
    .map(([k, v]) => {
      if (typeof v === "number" && Number.isFinite(v)) {
        return `${titleCase(k.replace(/_/g, " "))}: ${v}`;
      }
      if (typeof v === "string" && v.trim()) {
        return `${titleCase(k.replace(/_/g, " "))}: ${v.trim()}`;
      }
      return null;
    })
    .filter(Boolean)
    .join("\n");
}

/** Full-fidelity display text for one evidence pointer value (no aggressive truncation). */
export function formatEvidencePointerBody(pointer: string, value: unknown): string {
  if (value === undefined) {
    return "No matching evidence was found in the snapshot for this pointer.";
  }
  if (value === null) return "The snapshot value is empty.";
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || "The snapshot value is empty.";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const leaf = leafSegment(pointer);
    const normalized = normalizeJsonPointer(pointer);
    if (leaf === "win_rate" && value >= 0 && value <= 1) {
      return `Win rate: ${(value * 100).toFixed(1)}%`;
    }
    if (leaf === "sample_size") return `Sample size: ${Math.round(value)} trades`;
    if (leaf === "expectancy") return `Expectancy: ${value.toFixed(2)}`;
    if (leaf === "total_pnl") {
      const sign = value >= 0 ? "+" : "";
      return `Total PnL: ${sign}${value.toFixed(2)}`;
    }
    if (normalized.includes("pct_losers_strong_favorable_run_atr")) {
      return `${value.toFixed(0)}% of losers had a strong favorable run (≥0.8× ATR MFE).`;
    }
    if (normalized.includes("median_loser_favorable_move_atr")) {
      return `Median loser favorable move: ${value.toFixed(2)}× ATR.`;
    }
    if (normalized.includes("recommended_activation_mfe_atr_floor")) {
      return `Recommended trail activation floor: ${value.toFixed(2)}× ATR MFE.`;
    }
    return String(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === "string")) {
      return formatStringArray(value);
    }
    if (value.length === 0) return "The list is empty.";
    return formatStringArray(
      value.map((item) =>
        typeof item === "object" && item !== null
          ? JSON.stringify(item)
          : String(item ?? "")
      )
    );
  }

  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const parts = pointer.split("/").filter(Boolean);
    const leaf = parts[parts.length - 1] ?? "";

    if (parts.includes("management_actions") || leaf === "management_actions") {
      const actions = humanizeManagementActions(
        Array.isArray(value) ? (value as ManagementAction[]) : undefined
      );
      return actions.length ? actions.map((a) => `• ${a}`).join("\n") : "No management actions.";
    }

    if (parts.includes("price_movement_profile")) {
      return formatPriceMovementProfile(obj);
    }

    if (parts.includes("simulation_rule") && obj && typeof obj === "object") {
      const name = typeof obj.name === "string" ? obj.name.trim() : "";
      const kind = typeof obj.kind === "string" ? obj.kind.trim() : "";
      const lines = [name, kind].filter(Boolean);
      if (lines.length) return lines.join(" — ");
    }

    if (parts.includes("excursion_card") && typeof obj.headline === "string" && obj.headline.trim()) {
      return obj.headline.trim();
    }

    if (parts.includes("exit_placement_priority_leaks") && parts.includes("leaks")) {
      return formatExitPlacementPriorityLeak(obj);
    }

    if (parts.includes("swing_management_profile") || parts.includes("swing_behavior")) {
      return formatObjectFallback(obj);
    }

    return formatObjectFallback(obj);
  }

  return String(value);
}
